import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import Button from 'jbc-front/components/Button';
import ActionButton from 'jbc-front/components/ActionButton';
import { AddFile } from 'jbc-front/components/icons';
import BreadCrumb from 'jbc-front/components/BreadCrumb';
import LoadingView from 'src/components/LoadingView';
import SearchDetector from 'src/components/SearchDetector';
import SearchFormPaginator from 'src/components/SearchFormPaginator';
import { getCheckedDeclarations, clearCheckedDeclarations } from 'src/reducers/reports/incomeTaxDeclarations';
import { getIsLoading } from 'src/reducers/common/listReducer';
import { takeIncomeTaxDeclarationsSearchQueries, takeIncomeTaxDeclarationsSearchQueriesFromLocation } from 'src/reducers/searchForm';
import { REPORT_INDEX, INCOME_TAX_DECLARATION_NEW } from 'src/constants/EndpointUrls';

import IncomeTaxDeclarationTable from './IncomeTaxDeclarationTable';
import IncomeTaxDeclarationSearchForm from './IncomeTaxDeclarationSearchForm';
import BatchDeleteDeclarationModal from './BatchDeleteDeclarationModal';

import styles from './IncomeTaxDeclarationListPage.scss';

export const LIST_MODE = {
  SHOW: 'SHOW',
  EDIT: 'EDIT'
};

const getIncomeTaxDeclarationsLoading = state => getIsLoading('incomeTaxDeclarations', state);

const IncomeTaxDeclarationListPage = ({ declarations, formValues, location, onSearch, resetValues }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(LIST_MODE.SHOW);
  const [isBatchDeleteModalOpen, setIsBatchDeleteModalOpen] = useState(false);
  const checkedDeclarations = useSelector(getCheckedDeclarations);
  const isListLoading = useSelector(getIncomeTaxDeclarationsLoading);

  const dispatch = useDispatch();

  const changeToShowMode = () => setMode(LIST_MODE.SHOW);
  const changeToEditMode = () => setMode(LIST_MODE.EDIT);

  const handleCancel = () => {
    dispatch(clearCheckedDeclarations());
    changeToShowMode();
  };

  const handleNewDeclaration = () => {
    dispatch(push(INCOME_TAX_DECLARATION_NEW));
  };

  return (
    <div>
      <SearchDetector
        location={location}
        didMountDetectingJudger={() => true}
        willReceivePropsDetectingJudger={(prevProps, nextProps) => prevProps.location.search !== nextProps.location.search}
        onDetect={() =>
          onSearch(
            takeIncomeTaxDeclarationsSearchQueries({
              ...takeIncomeTaxDeclarationsSearchQueriesFromLocation(location),
              ...formValues
            })
          )
        }
      />
      <div className="l-main-title-wrap">
        <h1 className="m-title-main">{t('features.ReportIncomeTax.title')}</h1>
        <p className="m-title-main-note">{t('features.ReportIncomeTax.subtitle')}</p>
      </div>
      <div className="l-wrap-xl l-contents-wrap">
        <div>
          <BreadCrumb>
            <BreadCrumb.Item label={t('features.Reports.Report.create_document')} path={REPORT_INDEX} />
            <BreadCrumb.Item label={t('features.ReportIncomeTax.title')} />
          </BreadCrumb>
        </div>
        <IncomeTaxDeclarationSearchForm
          formName="incomeTaxDeclarationSearchForm"
          resetValues={resetValues}
          menuRender={() => (
            <div className="l-left-action">
              <ActionButton primary icon={<AddFile size={16} />} onClick={handleNewDeclaration} className={styles.newDeclarationButton}>
                {t('features.ReportIncomeTax.list.new_declaration')}
              </ActionButton>
            </div>
          )}
        />

        <LoadingView isLoading={isListLoading}>
          <div className={classNames('l-wrap-xl', styles.list)}>
            <div className="l-table-container">
              <IncomeTaxDeclarationTable declarations={declarations} mode={mode} />
            </div>

            <SearchFormPaginator formName="incomeTaxDeclarationSearchForm" reducerName="incomeTaxDeclarations" />

            {mode === LIST_MODE.EDIT ? (
              <div className={styles.batchDeleteButtons}>
                <Button className="u-mr20" primary disabled={!checkedDeclarations.length} onClick={() => setIsBatchDeleteModalOpen(true)}>
                  {t('features.ReportIncomeTax.list.bulk_delete.delete_button')}
                </Button>
                <Button onClick={handleCancel}>{t('features.ReportIncomeTax.list.bulk_delete.cancel')}</Button>
              </div>
            ) : (
              declarations &&
              declarations.length > 0 && (
                <div>
                  <a className="u-txt-link" onClick={changeToEditMode} role="presentation">
                    {t('features.ReportIncomeTax.list.bulk_delete.delete_in_bulk')}
                  </a>
                </div>
              )
            )}
          </div>
        </LoadingView>
      </div>

      <BatchDeleteDeclarationModal
        isOpen={isBatchDeleteModalOpen}
        hideModal={() => setIsBatchDeleteModalOpen(false)}
        changeToShowMode={changeToShowMode}
        checkedDeclarations={checkedDeclarations}
      />
    </div>
  );
};

IncomeTaxDeclarationListPage.propTypes = {
  declarations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      declarationName: PropTypes.string,
      office: PropTypes.string,
      formCode: PropTypes.string,
      periodType: PropTypes.string,
      periodMonth: PropTypes.number,
      periodYear: PropTypes.number,
      periodQuarter: PropTypes.number,
      exportedAt: PropTypes.string
    })
  ),
  formValues: PropTypes.shape({
    q: PropTypes.string,
    year: PropTypes.number,
    offices: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string
  }).isRequired,
  onSearch: PropTypes.func.isRequired,
  resetValues: PropTypes.shape({
    q: PropTypes.string,
    year: PropTypes.number,
    offices: PropTypes.string
  }).isRequired
};

IncomeTaxDeclarationListPage.defaultProps = {
  declarations: [],
  formValues: {}
};

export default IncomeTaxDeclarationListPage;
